import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import './Admin.css';

import config from '../config';
const API_URL = config.API_URL;

interface User {
    id: string;
    email: string;
    profile: {
        firstName: string;
        lastName: string;
        major: string;
        year: string;
        studentId: string;
        isInternational: boolean;
        country: string;
    };
    role: string;
    status: {
        isOnline: boolean;
        isActive: boolean;
        isVerified: boolean;
        isBanned?: boolean;
        banReason?: string;
        banExpires?: string;
    };
    stats: {
        reportCount: number;
    };
    createdAt: string;
}

interface Stats {
    overview: {
        totalUsers: number;
        onlineUsers: number;
        totalStudents: number;
        totalAdmins: number;
        verifiedUsers: number;
        internationalUsers: number;
        activeUsers: number;
        deactivatedUsers: number;
        reportedUsers: number;
    };
    content: {
        totalReviews: number;
        totalBookmarks: number;
        totalMessages: number;
    };
    growth: {
        newUsersThisWeek: number;
        newUsersThisMonth: number;
        dailyRegistrations: { _id: string; count: number }[];
    };
    demographics: {
        majorDistribution: { _id: string; count: number }[];
        yearDistribution: { _id: string; count: number }[];
        countryDistribution: { _id: string; count: number }[];
    };
}

interface Review {
    id: string;
    user: {
        id: string;
        name: string;
        email: string;
    };
    repoId: string;
    repoName: string;
    rating: number;
    comment: string;
    createdAt: string;
}

type TabType = 'overview' | 'users' | 'reviews' | 'reports' | 'activity' | 'settings';

const Admin: React.FC = () => {
    const navigate = useNavigate();
    const [users, setUsers] = useState<User[]>([]);
    const [stats, setStats] = useState<Stats | null>(null);
    const [reviews, setReviews] = useState<Review[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<TabType>('overview');
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterRole, setFilterRole] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [banModal, setBanModal] = useState<{ show: boolean; userId: string; userName: string }>({
        show: false,
        userId: '',
        userName: ''
    });
    const [banForm, setBanForm] = useState({ reason: '', duration: 0 });
    const [editModal, setEditModal] = useState<{ show: boolean; user: User | null }>({
        show: false,
        user: null
    });

    const token = localStorage.getItem('token');
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

    useEffect(() => {
        if (!token || currentUser.role !== 'admin') {
            toast.error('Access denied. Admin only.');
            navigate('/');
            return;
        }
        fetchAdminData();
    }, [token, currentUser.role, navigate]);

    const fetchAdminData = async () => {
        setLoading(true);
        try {
            const [statsRes, usersRes, reviewsRes] = await Promise.all([
                axios.get(`${API_URL}/admin/stats`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                axios.get(`${API_URL}/admin/users`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                axios.get(`${API_URL}/admin/reviews`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            ]);

            if (statsRes.data.success) setStats(statsRes.data.stats);
            if (usersRes.data.success) setUsers(usersRes.data.users);
            if (reviewsRes.data.success) setReviews(reviewsRes.data.reviews);
        } catch (error) {
            console.error('Failed to fetch admin data:', error);
            toast.error('Failed to load admin data');
        } finally {
            setLoading(false);
        }
    };

    // ==================== USER ACTIONS ====================

    const handleToggleUserStatus = async (userId: string, isActive: boolean) => {
        try {
            await axios.patch(
                `${API_URL}/admin/users/${userId}/status`,
                { isActive: !isActive },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setUsers(users.map(u =>
                u.id === userId ? { ...u, status: { ...u.status, isActive: !isActive } } : u
            ));
            toast.success(`User ${!isActive ? 'activated' : 'deactivated'}`);
        } catch (error) {
            toast.error('Failed to update user status');
        }
    };

    const handleDeleteUser = async (userId: string) => {
        if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;

        try {
            await axios.delete(`${API_URL}/admin/users/${userId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUsers(users.filter(u => u.id !== userId));
            toast.success('User deleted successfully');
        } catch (error) {
            toast.error('Failed to delete user');
        }
    };

    const handleBulkDelete = async () => {
        if (selectedUsers.length === 0) {
            toast.error('No users selected');
            return;
        }
        if (!window.confirm(`Are you sure you want to delete ${selectedUsers.length} users?`)) return;

        try {
            await axios.post(
                `${API_URL}/admin/users/bulk-delete`,
                { userIds: selectedUsers },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setUsers(users.filter(u => !selectedUsers.includes(u.id)));
            setSelectedUsers([]);
            toast.success(`${selectedUsers.length} users deleted`);
        } catch (error) {
            toast.error('Failed to delete users');
        }
    };

    const handleBanUser = async () => {
        try {
            await axios.patch(
                `${API_URL}/admin/users/${banModal.userId}/ban`,
                {
                    banned: true,
                    banReason: banForm.reason,
                    banDuration: banForm.duration || null
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setUsers(users.map(u =>
                u.id === banModal.userId
                    ? { ...u, status: { ...u.status, isBanned: true, isActive: false } }
                    : u
            ));
            setBanModal({ show: false, userId: '', userName: '' });
            setBanForm({ reason: '', duration: 0 });
            toast.success('User banned successfully');
        } catch (error) {
            toast.error('Failed to ban user');
        }
    };

    const handleUnbanUser = async (userId: string) => {
        try {
            await axios.patch(
                `${API_URL}/admin/users/${userId}/ban`,
                { banned: false },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setUsers(users.map(u =>
                u.id === userId
                    ? { ...u, status: { ...u.status, isBanned: false, isActive: true } }
                    : u
            ));
            toast.success('User unbanned');
        } catch (error) {
            toast.error('Failed to unban user');
        }
    };

    const handleChangeRole = async (userId: string, newRole: string) => {
        try {
            await axios.patch(
                `${API_URL}/admin/users/${userId}/role`,
                { role: newRole },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setUsers(users.map(u =>
                u.id === userId ? { ...u, role: newRole } : u
            ));
            toast.success(`Role changed to ${newRole}`);
        } catch (error) {
            toast.error('Failed to change role');
        }
    };

    const handleVerifyUser = async (userId: string) => {
        try {
            await axios.patch(
                `${API_URL}/admin/users/${userId}/verify`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setUsers(users.map(u =>
                u.id === userId ? { ...u, status: { ...u.status, isVerified: true } } : u
            ));
            toast.success('User verified');
        } catch (error) {
            toast.error('Failed to verify user');
        }
    };

    const handleResetReports = async (userId: string) => {
        try {
            await axios.patch(
                `${API_URL}/admin/users/${userId}/reset-reports`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setUsers(users.map(u =>
                u.id === userId ? { ...u, stats: { ...u.stats, reportCount: 0 } } : u
            ));
            toast.success('Reports reset');
        } catch (error) {
            toast.error('Failed to reset reports');
        }
    };

    // ==================== REVIEW ACTIONS ====================

    const handleDeleteReview = async (reviewId: string) => {
        if (!window.confirm('Delete this review?')) return;

        try {
            await axios.delete(`${API_URL}/admin/reviews/${reviewId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setReviews(reviews.filter(r => r.id !== reviewId));
            toast.success('Review deleted');
        } catch (error) {
            toast.error('Failed to delete review');
        }
    };

    // ==================== EXPORT ====================

    const handleExportUsers = async () => {
        try {
            const response = await axios.get(`${API_URL}/admin/export/users`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'users_export.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('Export downloaded');
        } catch (error) {
            toast.error('Failed to export');
        }
    };

    // ==================== HELPERS ====================

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const filteredUsers = users.filter(user => {
        const matchesSearch = searchQuery === '' ||
            user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.profile.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.profile.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.profile.studentId.includes(searchQuery);
        
        const matchesRole = filterRole === '' || user.role === filterRole;
        
        const matchesStatus = filterStatus === '' ||
            (filterStatus === 'active' && user.status.isActive) ||
            (filterStatus === 'inactive' && !user.status.isActive) ||
            (filterStatus === 'online' && user.status.isOnline) ||
            (filterStatus === 'banned' && user.status.isBanned) ||
            (filterStatus === 'reported' && user.stats.reportCount > 0);

        return matchesSearch && matchesRole && matchesStatus;
    });

    const toggleSelectUser = (userId: string) => {
        setSelectedUsers(prev =>
            prev.includes(userId)
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    const toggleSelectAll = () => {
        if (selectedUsers.length === filteredUsers.length) {
            setSelectedUsers([]);
        } else {
            setSelectedUsers(filteredUsers.map(u => u.id));
        }
    };

    if (loading) {
        return (
            <div className="admin-page">
                <div className="loading">Loading admin panel...</div>
            </div>
        );
    }

    return (
        <div className="admin-page">
            {/* Navigation */}
            <nav className="admin-nav">
                <div className="nav-container">
                    <Link to="/" className="nav-logo">
                        <img src="/pngegg.png" alt="NU Connect" className="nav-logo-img" />
                        <span>NU Connect</span>
                        <span className="admin-badge">Admin</span>
                    </Link>
                    <div className="nav-links">
                        <Link to="/" className="nav-link">Home</Link>
                        <Link to="/search" className="nav-link">Discover</Link>
                        <Link to="/chat" className="nav-link">Chat</Link>
                        <Link to="/profile" className="nav-link">Profile</Link>
                        <Link to="/admin" className="nav-link active">Admin Panel</Link>
                    </div>
                </div>
            </nav>

            <div className="admin-container">
                <div className="admin-header">
                    <h1>Admin Dashboard</h1>
                    <p>Manage users, content, and monitor platform activity</p>
                </div>

                {/* Stats Overview */}
                {stats && (
                    <div className="stats-grid">
                        <div className="stat-card">
                            <span className="stat-icon">👥</span>
                            <span className="stat-value">{stats.overview.totalUsers}</span>
                            <span className="stat-label">Total Users</span>
                        </div>
                        <div className="stat-card">
                            <span className="stat-icon">🟢</span>
                            <span className="stat-value">{stats.overview.onlineUsers}</span>
                            <span className="stat-label">Online Now</span>
                        </div>
                        <div className="stat-card">
                            <span className="stat-icon">📈</span>
                            <span className="stat-value">{stats.growth.newUsersThisWeek}</span>
                            <span className="stat-label">New This Week</span>
                        </div>
                        <div className="stat-card">
                            <span className="stat-icon">⭐</span>
                            <span className="stat-value">{stats.content.totalReviews}</span>
                            <span className="stat-label">Total Reviews</span>
                        </div>
                        <div className="stat-card">
                            <span className="stat-icon">💬</span>
                            <span className="stat-value">{stats.content.totalMessages}</span>
                            <span className="stat-label">Messages</span>
                        </div>
                        <div className="stat-card warning">
                            <span className="stat-icon">⚠️</span>
                            <span className="stat-value">{stats.overview.reportedUsers}</span>
                            <span className="stat-label">Reported Users</span>
                        </div>
                    </div>
                )}

                {/* Tabs */}
                <div className="admin-tabs">
                    {(['overview', 'users', 'reviews', 'reports', 'activity', 'settings'] as TabType[]).map(tab => (
                        <button
                            key={tab}
                            className={`tab ${activeTab === tab ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab)}
                        >
                            {tab.charAt(0).toUpperCase() + tab.slice(1)}
                            {tab === 'users' && ` (${users.length})`}
                            {tab === 'reviews' && ` (${reviews.length})`}
                            {tab === 'reports' && stats && ` (${stats.overview.reportedUsers})`}
                        </button>
                    ))}
                </div>

                {/* Tab Content */}
                <div className="admin-content">
                    {/* OVERVIEW TAB */}
                    {activeTab === 'overview' && stats && (
                        <div className="overview-section">
                            <div className="overview-grid">
                                {/* Demographics */}
                                <div className="info-card">
                                    <h3>📚 Major Distribution</h3>
                                    <div className="distribution-list">
                                        {stats.demographics.majorDistribution.map(item => (
                                            <div key={item._id} className="distribution-item">
                                                <span className="distribution-label">{item._id || 'Unknown'}</span>
                                                <div className="distribution-bar">
                                                    <div
                                                        className="distribution-fill"
                                                        style={{
                                                            width: `${(item.count / stats.overview.totalUsers) * 100}%`
                                                        }}
                                                    />
                                                </div>
                                                <span className="distribution-count">{item.count}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="info-card">
                                    <h3>🎓 Year Distribution</h3>
                                    <div className="distribution-list">
                                        {stats.demographics.yearDistribution.map(item => (
                                            <div key={item._id} className="distribution-item">
                                                <span className="distribution-label">{item._id || 'Unknown'}</span>
                                                <div className="distribution-bar">
                                                    <div
                                                        className="distribution-fill"
                                                        style={{
                                                            width: `${(item.count / stats.overview.totalUsers) * 100}%`
                                                        }}
                                                    />
                                                </div>
                                                <span className="distribution-count">{item.count}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="info-card">
                                    <h3>🌍 Top Countries</h3>
                                    <div className="distribution-list">
                                        {stats.demographics.countryDistribution.length > 0 ? (
                                            stats.demographics.countryDistribution.map(item => (
                                                <div key={item._id} className="distribution-item">
                                                    <span className="distribution-label">{item._id || 'USA'}</span>
                                                    <span className="distribution-count">{item.count}</span>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="no-data">No international students yet</p>
                                        )}
                                    </div>
                                </div>

                                <div className="info-card">
                                    <h3>📊 Quick Stats</h3>
                                    <div className="quick-stats">
                                        <div className="quick-stat">
                                            <span>Students</span>
                                            <strong>{stats.overview.totalStudents}</strong>
                                        </div>
                                        <div className="quick-stat">
                                            <span>Admins</span>
                                            <strong>{stats.overview.totalAdmins}</strong>
                                        </div>
                                        <div className="quick-stat">
                                            <span>Verified</span>
                                            <strong>{stats.overview.verifiedUsers}</strong>
                                        </div>
                                        <div className="quick-stat">
                                            <span>International</span>
                                            <strong>{stats.overview.internationalUsers}</strong>
                                        </div>
                                        <div className="quick-stat">
                                            <span>Active</span>
                                            <strong>{stats.overview.activeUsers}</strong>
                                        </div>
                                        <div className="quick-stat">
                                            <span>Deactivated</span>
                                            <strong>{stats.overview.deactivatedUsers}</strong>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* USERS TAB */}
                    {activeTab === 'users' && (
                        <div className="users-section">
                            {/* Toolbar */}
                            <div className="users-toolbar">
                                <div className="search-filter">
                                    <input
                                        type="text"
                                        placeholder="Search users..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="search-input"
                                    />
                                    <select
                                        value={filterRole}
                                        onChange={(e) => setFilterRole(e.target.value)}
                                        className="filter-select"
                                    >
                                        <option value="">All Roles</option>
                                        <option value="student">Students</option>
                                        <option value="admin">Admins</option>
                                    </select>
                                    <select
                                        value={filterStatus}
                                        onChange={(e) => setFilterStatus(e.target.value)}
                                        className="filter-select"
                                    >
                                        <option value="">All Status</option>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                        <option value="online">Online</option>
                                        <option value="banned">Banned</option>
                                        <option value="reported">Reported</option>
                                    </select>
                                </div>
                                <div className="bulk-actions">
                                    {selectedUsers.length > 0 && (
                                        <>
                                            <span className="selected-count">
                                                {selectedUsers.length} selected
                                            </span>
                                            <button
                                                onClick={handleBulkDelete}
                                                className="btn-action delete"
                                            >
                                                Delete Selected
                                            </button>
                                        </>
                                    )}
                                    <button onClick={handleExportUsers} className="btn-action export">
                                        📥 Export CSV
                                    </button>
                                </div>
                            </div>

                            {/* Users Table */}
                            <table className="users-table">
                                <thead>
                                    <tr>
                                        <th>
                                            <input
                                                type="checkbox"
                                                checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                                                onChange={toggleSelectAll}
                                            />
                                        </th>
                                        <th>User</th>
                                        <th>Email</th>
                                        <th>Role</th>
                                        <th>Status</th>
                                        <th>Reports</th>
                                        <th>Joined</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredUsers.map(user => (
                                        <tr key={user.id} className={user.status.isBanned ? 'banned-row' : ''}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedUsers.includes(user.id)}
                                                    onChange={() => toggleSelectUser(user.id)}
                                                    disabled={user.id === currentUser.id}
                                                />
                                            </td>
                                            <td>
                                                <Link to={`/profile/${user.id}`} className="user-link">
                                                    <div className="user-avatar">
                                                        {user.profile.firstName[0]}{user.profile.lastName[0]}
                                                    </div>
                                                    <div>
                                                        <span className="user-name">
                                                            {user.profile.firstName} {user.profile.lastName}
                                                        </span>
                                                        <span className="user-major">{user.profile.major}</span>
                                                    </div>
                                                </Link>
                                            </td>
                                            <td>{user.email}</td>
                                            <td>
                                                <select
                                                    value={user.role}
                                                    onChange={(e) => handleChangeRole(user.id, e.target.value)}
                                                    className={`role-select ${user.role}`}
                                                    disabled={user.id === currentUser.id}
                                                >
                                                    <option value="student">Student</option>
                                                    <option value="admin">Admin</option>
                                                </select>
                                            </td>
                                            <td>
                                                <div className="status-badges">
                                                    {user.status.isBanned ? (
                                                        <span className="status-badge banned">🚫 Banned</span>
                                                    ) : user.status.isActive ? (
                                                        <span className="status-badge active">Active</span>
                                                    ) : (
                                                        <span className="status-badge inactive">Inactive</span>
                                                    )}
                                                    {user.status.isOnline && (
                                                        <span className="online-indicator">🟢</span>
                                                    )}
                                                    {user.status.isVerified && (
                                                        <span className="verified-indicator">✓</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                {user.stats.reportCount > 0 ? (
                                                    <span className="report-count">
                                                        ⚠️ {user.stats.reportCount}
                                                        <button
                                                            onClick={() => handleResetReports(user.id)}
                                                            className="btn-tiny"
                                                            title="Reset reports"
                                                        >
                                                            ✕
                                                        </button>
                                                    </span>
                                                ) : (
                                                    <span className="no-reports">0</span>
                                                )}
                                            </td>
                                            <td>{formatDate(user.createdAt)}</td>
                                            <td>
                                                <div className="action-buttons">
                                                    {!user.status.isVerified && (
                                                        <button
                                                            onClick={() => handleVerifyUser(user.id)}
                                                            className="btn-action verify"
                                                            title="Verify user"
                                                        >
                                                            ✓
                                                        </button>
                                                    )}
                                                    {user.status.isBanned ? (
                                                        <button
                                                            onClick={() => handleUnbanUser(user.id)}
                                                            className="btn-action unban"
                                                        >
                                                            Unban
                                                        </button>
                                                    ) : (
                                                        <>
                                                            <button
                                                                onClick={() =>handleToggleUserStatus(user.id, user.status.isActive)
                                                            }
                                                            className={`btn-action ${user.status.isActive ? 'deactivate' : 'activate'}`}
                                                            disabled={user.id === currentUser.id}
                                                        >
                                                            {user.status.isActive ? 'Deactivate' : 'Activate'}
                                                        </button>
                                                        <button
                                                            onClick={() =>
                                                                setBanModal({
                                                                    show: true,
                                                                    userId: user.id,
                                                                    userName: `${user.profile.firstName} ${user.profile.lastName}`
                                                                })
                                                            }
                                                            className="btn-action ban"
                                                            disabled={user.id === currentUser.id}
                                                        >
                                                            Ban
                                                        </button>
                                                    </>
                                                )}
                                                <button
                                                    onClick={() => handleDeleteUser(user.id)}
                                                    className="btn-action delete"
                                                    disabled={user.id === currentUser.id}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* REVIEWS TAB */}
                {activeTab === 'reviews' && (
                    <div className="reviews-section">
                        <h3>All Reviews</h3>
                        {reviews.length > 0 ? (
                            <table className="reviews-table">
                                <thead>
                                    <tr>
                                        <th>User</th>
                                        <th>Repository</th>
                                        <th>Rating</th>
                                        <th>Comment</th>
                                        <th>Date</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reviews.map(review => (
                                        <tr key={review.id}>
                                            <td>
                                                <Link to={`/profile/${review.user.id}`}>
                                                    {review.user.name}
                                                </Link>
                                            </td>
                                            <td>
                                                <Link to={`/details/${review.repoId}?name=${encodeURIComponent(review.repoName)}`}>
                                                    {review.repoName}
                                                </Link>
                                            </td>
                                            <td>{'⭐'.repeat(review.rating)}</td>
                                            <td className="review-comment-cell">{review.comment}</td>
                                            <td>{formatDate(review.createdAt)}</td>
                                            <td>
                                                <button
                                                    onClick={() => handleDeleteReview(review.id)}
                                                    className="btn-action delete"
                                                >
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <p className="no-data">No reviews yet</p>
                        )}
                    </div>
                )}

                {/* REPORTS TAB */}
                {activeTab === 'reports' && (
                    <div className="reports-section">
                        <h3>Reported Users</h3>
                        {users.filter(u => u.stats.reportCount > 0).length > 0 ? (
                            <table className="users-table">
                                <thead>
                                    <tr>
                                        <th>User</th>
                                        <th>Email</th>
                                        <th>Report Count</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users
                                        .filter(u => u.stats.reportCount > 0)
                                        .sort((a, b) => b.stats.reportCount - a.stats.reportCount)
                                        .map(user => (
                                            <tr key={user.id}>
                                                <td>
                                                    <Link to={`/profile/${user.id}`} className="user-link">
                                                        <div className="user-avatar">
                                                            {user.profile.firstName[0]}{user.profile.lastName[0]}
                                                        </div>
                                                        <span className="user-name">
                                                            {user.profile.firstName} {user.profile.lastName}
                                                        </span>
                                                    </Link>
                                                </td>
                                                <td>{user.email}</td>
                                                <td>
                                                    <span className="report-badge">
                                                        ⚠️ {user.stats.reportCount} reports
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`status-badge ${user.status.isActive ? 'active' : 'inactive'}`}>
                                                        {user.status.isBanned ? 'Banned' : user.status.isActive ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="action-buttons">
                                                        <button
                                                            onClick={() => handleResetReports(user.id)}
                                                            className="btn-action"
                                                        >
                                                            Clear Reports
                                                        </button>
                                                        <button
                                                            onClick={() =>
                                                                setBanModal({
                                                                    show: true,
                                                                    userId: user.id,
                                                                    userName: `${user.profile.firstName} ${user.profile.lastName}`
                                                                })
                                                            }
                                                            className="btn-action ban"
                                                        >
                                                            Ban User
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="empty-state">
                                <span className="empty-icon">✅</span>
                                <p>No reported users. Everything looks good!</p>
                            </div>
                        )}
                    </div>
                )}

                {/* ACTIVITY TAB */}
                {activeTab === 'activity' && (
                    <div className="activity-section">
                        <h3>Recent Activity</h3>
                        <p className="placeholder-text">
                            Activity logs will show recent registrations, messages, and user actions.
                            <br />
                            <em>Coming soon with real-time updates!</em>
                        </p>
                    </div>
                )}

                {/* SETTINGS TAB */}
                {activeTab === 'settings' && (
                    <div className="settings-section">
                        <h3>Platform Settings</h3>
                        <div className="settings-grid">
                            <div className="setting-card">
                                <h4>📧 Email Verification</h4>
                                <p>Require .edu email verification for registration</p>
                                <label className="toggle">
                                    <input type="checkbox" defaultChecked />
                                    <span className="slider"></span>
                                </label>
                            </div>
                            <div className="setting-card">
                                <h4>🔒 Student ID Required</h4>
                                <p>Require valid student ID during registration</p>
                                <label className="toggle">
                                    <input type="checkbox" defaultChecked />
                                    <span className="slider"></span>
                                </label>
                            </div>
                            <div className="setting-card">
                                <h4>🚫 Auto-ban Threshold</h4>
                                <p>Automatically ban users after X reports</p>
                                <input type="number" defaultValue={5} min={1} max={20} />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>

        {/* Ban Modal */}
        {banModal.show && (
            <div className="modal-overlay">
                <div className="modal">
                    <h3>Ban User</h3>
                    <p>You are about to ban: <strong>{banModal.userName}</strong></p>
                    <div className="form-group">
                        <label>Reason</label>
                        <textarea
                            value={banForm.reason}
                            onChange={(e) => setBanForm({ ...banForm, reason: e.target.value })}
                            placeholder="Enter reason for ban..."
                        />
                    </div>
                    <div className="form-group">
                        <label>Duration (days, 0 = permanent)</label>
                        <input
                            type="number"
                            value={banForm.duration}
                            onChange={(e) => setBanForm({ ...banForm, duration: parseInt(e.target.value) || 0 })}
                            min={0}
                        />
                    </div>
                    <div className="modal-actions">
                        <button
                            onClick={() => setBanModal({ show: false, userId: '', userName: '' })}
                            className="btn-secondary"
                        >
                            Cancel
                        </button>
                        <button onClick={handleBanUser} className="btn-danger">
                            Ban User
                        </button>
                    </div>
                </div>
            </div>
        )}
    </div>
);
};

export default Admin;